import React, { useState } from 'react';
import { Paginator } from 'primereact/paginator';
import { CheckCircle2, Package, Clock, AlertTriangle, PlayCircle } from 'lucide-react';

export const LiveActionLogsTable = ({ logs = [], loading, isLiveActive }: any) => {
  // Sayfalama stateleri (DataTable yerine manuel yönetiyoruz)
  const [first, setFirst] = useState(0);
  const rows = 12; // Grid yapısı için 2, 3 ve 4'e tam bölünebilen 12 idealdir.

  // İsimden baş harf üreten yardımcı fonksiyon
  const getInitials = (name: string) => {
    if (!name) return "P";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2);
  };

  // Sadece o anki sayfada gösterilecek veriler
  const currentLogs = logs.slice(first, first + rows);

  // Aksiyon tipi yapılandırması (Renkler ve İkonlar)
  const getActionConfig = (actionType: string) => {
    switch (actionType) {
      case 'PICKING_DONE':
        return {
          icon: <CheckCircle2 size={13} />,
          accent: 'bg-green-500',
          badgeBg: 'bg-green-500/10',
          badgeText: 'text-green-600 dark:text-green-400',
          label: 'Toplama Tamamlandı'
        };
      case 'PACKAGING_DONE':
        return {
          icon: <Package size={13} />,
          accent: 'bg-blue-500',
          badgeBg: 'bg-blue-500/10',
          badgeText: 'text-blue-600 dark:text-blue-400',
          label: 'Paketleme Tamamlandı'
        };
      case 'SHORTAGE_REPORTED':
        return {
          icon: <AlertTriangle size={13} />,
          accent: 'bg-red-500',
          badgeBg: 'bg-red-500/10',
          badgeText: 'text-red-600 dark:text-red-400',
          label: 'Eksik Bildirildi'
        };
      case 'ASSIGNED':
      case 'PACKAGING_START':
      case 'RESUMED':
        return {
          icon: <PlayCircle size={13} />,
          accent: 'bg-amber-500',
          badgeBg: 'bg-amber-500/10',
          badgeText: 'text-amber-600 dark:text-amber-400',
          label: 'İşleme Başlandı'
        };
      default:
        return {
          icon: <Clock size={13} />,
          accent: 'bg-gray-400',
          badgeBg: 'bg-gray-500/10',
          badgeText: 'text-gray-600 dark:text-gray-300',
          label: actionType || 'Bilinmeyen İşlem'
        };
    }
  };

  // Yükleniyor (Skeleton) Görünümü
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-pulse p-1">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-28 bg-card border border-borderColor/40 rounded-2xl" />
        ))}
      </div>
    );
  }

  // Veri Yok Görünümü
  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-borderColor/50 rounded-2xl bg-black/5 dark:bg-white/5">
        <Clock size={32} className="opacity-20 mb-3" />
        <span className="text-sm font-medium opacity-60" style={{ color: 'var(--text)' }}>
          Seçilen kriterlere uygun işlem kaydı bulunamadı.
        </span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 p-1 rounded-2xl transition-all duration-300 ${isLiveActive ? 'ring-2 ring-green-500/20 bg-green-500/5' : ''}`}>
      
      {/* KART GRID ALANI */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {currentLogs.map((log: any) => {
          const config = getActionConfig(log.actionType);

          return (
            <div 
              key={log.id} 
              className="group relative flex flex-col gap-3 p-3.5 bg-card border border-borderColor/40 hover:border-borderColor rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden"
            >
              {/* Sol taraftaki ince renkli çizgi (Aksiyona göre değişir) */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${config.accent} opacity-80 group-hover:opacity-100 transition-opacity`} />

              {/* Üst Kısım: Personel & Tarih */}
              <div className="flex justify-between items-start pl-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
                    {getInitials(log.user?.name)}
                  </div>
                  <span className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>
                    {log.user?.name || 'Bilinmeyen Personel'}
                  </span>
                </div>
                <div className="text-[10px] font-mono opacity-50 shrink-0 mt-0.5" style={{ color: 'var(--text)' }}>
                  {new Date(log.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
              </div>

              {/* Orta Kısım: Sipariş Bilgisi */}
              <div className="pl-2">
                <div className="text-sm font-black tracking-tight" style={{ color: 'var(--text)' }}>
                  {log.task?.order?.orderNumber || '#SİPARİŞ-YOK'}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-60 font-semibold mt-0.5" style={{ color: 'var(--text)' }}>
                  Task Status: {log.task?.status || '-'}
                </div>
              </div>

              {/* Alt Kısım: İşlem Rozeti */}
              <div className="pl-2 mt-auto pt-1">
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold tracking-wide uppercase ${config.badgeBg} ${config.badgeText}`}>
                  {config.icon}
                  <span>{config.label}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* SAYFALAMA */}
      {logs.length > rows && (
        <div className="mt-2 border-t border-borderColor/30 pt-2">
          <Paginator 
            first={first} 
            rows={rows} 
            totalRecords={logs.length} 
            onPageChange={(e) => setFirst(e.first)} 
            className="bg-transparent border-none p-0 [&_.p-paginator-page]:text-xs [&_.p-paginator-page]:w-7 [&_.p-paginator-page]:h-7"
          />
        </div>
      )}
    </div>
  );
};